import type { DriverDeviceView } from '@chukua/common';
import { Button, Tag } from 'antd';
import { Smartphone } from 'lucide-react';
import { EmptyState } from '../../../shared/components/EmptyState';
import type { DeviceAction } from '../hooks/use-driver-devices';
import { deviceName } from '../policies/device-event-labels';

interface CurrentDeviceCardProps {
  device: DriverDeviceView | null;
  onAct: (action: DeviceAction) => void;
}

function WipeStatus({ wipe }: { wipe: DriverDeviceView['wipe'] }) {
  if (wipe.state === 'none') return null;
  const by = wipe.requestedByName ? ` by ${wipe.requestedByName}` : '';
  const requested = wipe.requestedAt ? ` on ${new Date(wipe.requestedAt).toLocaleString()}` : '';
  return (
    <div className="mt-3 flex flex-col gap-1 text-sm">
      {wipe.state === 'pending' ? <Tag color="orange">Wipe pending</Tag> : <Tag color="blue">Wipe sent to the phone</Tag>}
      <span className="text-muted">
        Requested{by}
        {requested}
        {wipe.reason ? `: ${wipe.reason}` : ''}
      </span>
      <span className="text-muted">
        {wipe.state === 'pending'
          ? 'The phone is told the next time it contacts the server.'
          : `Told at ${wipe.deliveredAt ? new Date(wipe.deliveredAt).toLocaleString() : '—'}. It is repeated on every call until the driver signs in again.`}
      </span>
    </div>
  );
}

/** The driver's one phone: who they are signed in on, when it last called, and the two things the office can do to it. */
export function CurrentDeviceCard({ device, onAct }: CurrentDeviceCardProps) {
  if (!device) {
    return <EmptyState icon={Smartphone} title="No phone yet" description="The driver hasn't signed in to the app, so there is no device to manage." />;
  }

  const canWipe = device.isActive && device.wipe.state === 'none';
  return (
    <section aria-label="Current device" className="rounded-lg border border-line bg-surface px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Smartphone aria-hidden className="size-4 text-muted" />
          <p className="text-sm font-medium text-fg">{deviceName(device)}</p>
          <Tag color={device.isActive ? 'green' : 'default'}>{device.isActive ? 'Signed in' : 'Signed out'}</Tag>
        </div>
        <div className="flex gap-2">
          <Button size="small" disabled={!device.isActive} onClick={() => onAct('revoke')}>
            Revoke
          </Button>
          <Button size="small" danger disabled={!canWipe} onClick={() => onAct('wipe')}>
            Wipe
          </Button>
        </div>
      </div>
      <p className="mt-2 text-sm text-muted">
        {[device.platform, device.appVersion ? `App ${device.appVersion}` : null, `Signed in ${new Date(device.signedInAt).toLocaleString()}`]
          .filter(Boolean)
          .join(' · ')}
      </p>
      <p className="text-sm text-muted">{device.lastSeenAt ? `Last seen ${new Date(device.lastSeenAt).toLocaleString()}` : 'Not seen since signing in'}</p>
      <WipeStatus wipe={device.wipe} />
    </section>
  );
}
